<template>
  <main v-if="isGameInitialized" class="puzzle game">
    <template v-if="!isGameFinished">
      <section>
        <div class="top_bar">
          <div class="time_counter_box flex-row">
            <GameTimer
              ref="game-timer-ref"
              :initial-value-in-seconds="appSettingsStore.appProperties.timerForPuzzleGame"
              :start-immediate="false"
              @time:expired="handleTimeExpired"
            />
          </div>
        </div>
      </section>

      <section class="game_box">
        <div id="container">
          <div class="box_puzzle">
            <div v-if="!isGameInProgress" class="me full"></div>
          </div>

          <div class="pre_img prevent_click">
            <li data-bid="0">
              <img src="" alt="preview" />
            </li>
          </div>
        </div>

        <BaseButton
          v-if="isStartGameButtonVisible"
          container-class="start"
          @click:button="handleStartGame"
        >
          {{ $_translations.value.page_puzzle_button_play }}
        </BaseButton>
      </section>
    </template>
  </main>

  <BaseLoaderModal v-if="isLoaderModalOpen" />

  <MaintenanceModal
    v-else-if="isMaintenanceModalOpen"
    @redirect:homePage="handleRedirectToHomePageFromMaintenanceModal"
  />

  <BonusGameAlreadyPlayedModal v-else-if="isBonusGameAlreadyPlayedModalOpen" />

  <PostGamePuzzleModal
    v-else-if="isPostGamePuzzleModalOpen"
    :successful="postGameInfo.successful"
    :prize-value="postGameInfo.prizeValue"
    @close="handleClosePostGameModal"
  />
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef } from 'vue';
import $ from 'jquery';
import BaseButton from '@/components/button/BaseButton.vue';
import GameTimer from '@/components/timer/GameTimer.vue';
import BaseLoaderModal from '@/components/modal/loader/BaseLoaderModal.vue';
import MaintenanceModal from '@/components/modal/MaintenanceModal.vue';
import BonusGameAlreadyPlayedModal
  from '@/views/bonusGame/components/modal/BonusGameAlreadyPlayedModal.vue';
import PostGamePuzzleModal from '@/views/bonusGame/components/modal/PostGamePuzzleModal.vue';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import useAuthStore from '@/store/modules/auth.js';
import useBonusPhaseStore from '@/store/modules/bonusPhase.js';
import usePageNavigationHook from '@/hooks/usePageNavigation.js';
import useModalHook from '@/hooks/useModal.js';
import useBonusGameHook from '@/hooks/useBonusGame.js';
import { generateRandomNumberInRange } from '@/helpers/common.js';
import { apiFinishBonusGame } from '@/services/game.js';
import { apiResponseTypeEnum } from '@/constants/api.js';

const imageList = [...Array(6).keys()].map((id) => {
  const filename = (id + 1).toString().padStart(2, '0');

  return `/static/images/puzzle/${ filename }.jpg`;
});

const originalTilePositionList = [0, 1, 2, 3, 4, 5, 6, 7, 8];

const appSettingsStore = useAppSettingsStore();
const authStore = useAuthStore();
const bonusPhaseStore = useBonusPhaseStore();

const { redirectToHomePage } = usePageNavigationHook();
const [isLoaderModalOpen, openLoaderModal, closeLoaderModal] = useModalHook();
const [isMaintenanceModalOpen, openMaintenanceModal, closeMaintenanceModal] = useModalHook();
const [isBonusGameAlreadyPlayedModalOpen, openBonusGameAlreadyPlayedModal] = useModalHook();
const [isPostGamePuzzleModalOpen, openPostGamePuzzleModal, closePostGamePuzzleModal] = useModalHook();
const { addPuzzleStylesheet, removePuzzleStylesheet } = useBonusGameHook();

const gameTimerRef = useTemplateRef('game-timer-ref');

const isGameInitialized = ref(false);
const isGameInProgress = ref(false);
const isGameFinished = ref(false);
const selectedImage = ref('');
const shuffledTilePositionList = ref([...originalTilePositionList]);
const postGameInfo = ref({ successful: false, prizeValue: 0 });

const isStartGameButtonVisible = computed(() => !isGameInProgress.value && !isGameFinished.value);

const isPuzzleSolved = () => JSON.stringify(originalTilePositionList) === JSON.stringify(shuffledTilePositionList.value);

const generateRandomizedTilesFromImage = () => {
  const cloneTilePositionList = [...originalTilePositionList];

  let randomPosition = null;

  for (let i = originalTilePositionList.length - 1; i >= 0; i--) {
    randomPosition = generateRandomNumberInRange(0, i);

    [cloneTilePositionList[i], cloneTilePositionList[randomPosition]] = [cloneTilePositionList[randomPosition], cloneTilePositionList[i]];
    shuffledTilePositionList.value[i] = cloneTilePositionList[i];
  }

  const elContainer = $('.box_puzzle');

  for (let i = 0; i < originalTilePositionList.length; i++) {
    const elClass = `me me_${ shuffledTilePositionList.value[i] } tile is_clickable`;
    const elStyle = `background-image: url("${ selectedImage.value }");`;

    elContainer.append(`<div class='${ elClass }' style='${ elStyle }' data-bid='${ shuffledTilePositionList.value[i] }' ></div>`);

    if (((i + 1) % 3) === 0) {
      elContainer.append('<br>');
    }
  }
};

const gameOver = async (isSolved) => {
  try {
    isGameFinished.value = true;
    openLoaderModal({ hideManually: true });

    const payload = {
      successful: isSolved,
      achievementName: '',
      gameId: bonusPhaseStore.bonusPhaseGameConfig.gameId,
      prize: bonusPhaseStore.bonusPhaseGameConfig.prizeValue,
      gameType: 'PUZZLE',
    };

    await apiFinishBonusGame({ payload });

    await authStore.dispatchRetrieveAuthUserLoginDetails().catch(() => {
    });

    await closeLoaderModal({ immediate: true });

    postGameInfo.value.successful = isSolved;
    postGameInfo.value.prizeValue = bonusPhaseStore.bonusPhaseGameConfig.prizeValue;

    openPostGamePuzzleModal({
      hideImmediate: true,
      afterHideCallback: redirectToHomePage,
    });
  } catch (error) {
    await closeLoaderModal({ immediate: true });

    const modalArgs = {
      showImmediate: true,
      hideImmediate: true,
      afterHideCallback: redirectToHomePage,
    };

    if (error?.responseEnum === apiResponseTypeEnum.ALREADY_PLAYED) {
      openBonusGameAlreadyPlayedModal(modalArgs);
    } else {
      openMaintenanceModal(modalArgs);
    }
  } finally {
    bonusPhaseStore.resetBonusPhaseGameConfigState();
  }
};

const registerTileClickListener = () => {
  let firstSelectedTileOriginalPosition = null;
  let secondSelectedTileOriginalPosition = null;
  let hasPendingTileSwap = false;

  const getCurrentTileOriginalPosition = (elSelector) => parseInt($(elSelector).attr('data-bid'), 10);

  const swapTilesInHtml = (firstElSelector, secondElSelector, firstTileOriginalPosition, secondTileOriginalPosition) => {
    const updateTileHtmlAttributes = (elSelector, currentOriginalPosition, targetOriginalPosition) => {
      $(elSelector)
        .addClass('me_' + targetOriginalPosition)
        .removeClass('me_' + currentOriginalPosition)
        .attr('data-bid', targetOriginalPosition);
    };

    updateTileHtmlAttributes(firstElSelector, firstTileOriginalPosition, secondTileOriginalPosition);
    updateTileHtmlAttributes(secondElSelector, secondTileOriginalPosition, firstTileOriginalPosition);
  };

  const swapTilesInShuffledList = (firstTileOriginalPosition, secondTileOriginalPosition) => {
    const firstTileShuffledPosition = shuffledTilePositionList.value.indexOf(firstTileOriginalPosition);
    const secondTileShuffledPosition = shuffledTilePositionList.value.indexOf(secondTileOriginalPosition);

    shuffledTilePositionList.value[firstTileShuffledPosition] = secondTileOriginalPosition;
    shuffledTilePositionList.value[secondTileShuffledPosition] = firstTileOriginalPosition;
  };

  const validateTile = (originalPosition) => {
    if (shuffledTilePositionList.value.indexOf(originalPosition) !== originalPosition) { return; }

    $(`.me_${ originalPosition }`).removeClass('is_clickable').addClass('correct_puzzle prevent_click');
  };

  $('.me').click(function () {
    if (!hasPendingTileSwap) {
      firstSelectedTileOriginalPosition = getCurrentTileOriginalPosition(this);

      $(`.me_${ firstSelectedTileOriginalPosition }`).css({ opacity: '.65' });

      hasPendingTileSwap = true;
    } else {
      secondSelectedTileOriginalPosition = getCurrentTileOriginalPosition(this);

      $(`.me_${ firstSelectedTileOriginalPosition }`).css({ opacity: '1' });

      if (firstSelectedTileOriginalPosition !== secondSelectedTileOriginalPosition) {
        swapTilesInHtml(`.me_${ firstSelectedTileOriginalPosition }`, this, firstSelectedTileOriginalPosition, secondSelectedTileOriginalPosition);

        swapTilesInShuffledList(firstSelectedTileOriginalPosition, secondSelectedTileOriginalPosition);
      }

      validateTile(firstSelectedTileOriginalPosition);
      if (firstSelectedTileOriginalPosition !== secondSelectedTileOriginalPosition) {
        validateTile(secondSelectedTileOriginalPosition);
      }

      firstSelectedTileOriginalPosition = null;
      secondSelectedTileOriginalPosition = null;
      hasPendingTileSwap = false;
    }

    if (isPuzzleSolved()) {
      gameTimerRef.value?.stopTimer?.();
      gameOver(true);
    }
  });
};

const handleStartGame = () => {
  isGameInProgress.value = true;

  generateRandomizedTilesFromImage();

  gameTimerRef.value?.startTimer?.();

  registerTileClickListener();
};

const handleTimeExpired = () => {
  gameOver(false);
};

const handleRedirectToHomePageFromMaintenanceModal = async () => {
  await closeMaintenanceModal({ immediate: true });

  redirectToHomePage();
};

const handleClosePostGameModal = async () => {
  closePostGamePuzzleModal({ immediate: true });

  redirectToHomePage();
};

const initGame = async () => {
  const selectedImageIndex = generateRandomNumberInRange(0, imageList.length - 1);

  selectedImage.value = imageList[selectedImageIndex];

  if (!selectedImage.value) {
    openMaintenanceModal({
      hideImmediate: true,
      afterHideCallback: redirectToHomePage,
    });

    return;
  }

  await addPuzzleStylesheet();

  isGameInitialized.value = true;

  await nextTick();

  $('.me').css({ 'background-image': `url(${ selectedImage.value })` });
  $('.pre_img li img').attr('src', selectedImage.value);
};

onMounted(() => {
  if (appSettingsStore.appProperties.timerForPuzzleGame) {
    initGame();
  } else {
    openMaintenanceModal({
      hideImmediate: true,
      afterHideCallback: redirectToHomePage,
    });
  }
});

onUnmounted(removePuzzleStylesheet);
</script>
